import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { FLOORPLANS, UNITS } from "../../data/project";

const UNIT_KEYS = ["th3", "th4", "gh4", "rv5", "fv6", "gm6"]; // displayed in price order

const FLOOR_META = {
  basement: { label: "Basement", level: "Level B1", focus: "Wellness & Cinema" },
  ground: { label: "Ground Floor", level: "Level 00", focus: "Living & Entertaining" },
  first: { label: "First Floor", level: "Level 01", focus: "Bedrooms" },
  roof: { label: "Roof Terrace", level: "Level 02", focus: "Entertainment" },
};

export default function FloorPlans() {
  const [unitKey, setUnitKey] = useState("th3");
  const [floor, setFloor] = useState("ground");

  const fp = FLOORPLANS[unitKey];

  // Build available floors dynamically based on data
  const availableFloors = useMemo(() => {
    const list = [];
    if (fp.basementFloor) list.push("basement");
    if (fp.groundFloor) list.push("ground");
    if (fp.firstFloor) list.push("first");
    if (fp.roofFloor) list.push("roof");
    return list;
  }, [fp]);

  // If switching to a unit that doesn't have current floor, fall back
  const safeFloor = availableFloors.includes(floor) ? floor : availableFloors[0];

  const imageMap = {
    basement: fp.basementFloor,
    ground: fp.groundFloor,
    first: fp.firstFloor,
    roof: fp.roofFloor,
  };
  const roomsMap = {
    basement: fp.basementFloorRooms,
    ground: fp.groundFloorRooms,
    first: fp.firstFloorRooms,
    roof: fp.roofFloorRooms,
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleUnitChange = (key) => {
    setUnitKey(key);
    // reset to ground floor when switching
    setFloor("ground");
  };

  return (
    <section
      id="floorplans"
      data-testid="floorplans-section"
      className="relative bg-[#F4F2EC] py-24 md:py-36 border-t hairline"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-8 items-end mb-12 md:mb-16">
          <div className="col-span-12 md:col-span-7">
            <div className="overline mb-5">Floor Plans</div>
            <h2
              className="font-serif leading-[1.02] tracking-tight text-[#1A3626]"
              style={{ fontSize: "clamp(40px, 5.5vw, 78px)" }}
            >
              Inside the
              <br />
              <span className="italic font-light">residences.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5">
            <p className="text-[#1A3626]/75 font-light leading-relaxed">
              Considered, generous, contemporary. Browse the layouts of the
              full collection. From the entry-level{" "}
              <strong className="font-medium">3-Bedroom Townhome</strong> to
              the four-level{" "}
              <strong className="font-medium">6-Bedroom Golf Mansion</strong>.
            </p>
          </div>
        </div>

        {/* Unit selector tabs */}
        <div
          role="tablist"
          aria-label="Unit selector"
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border-y hairline mb-10"
        >
          {UNIT_KEYS.map((key) => {
            const u = UNITS.find((x) => x.id === key);
            const isActive = unitKey === key;
            return (
              <button
                key={key}
                role="tab"
                aria-selected={isActive}
                data-testid={`fp-unit-tab-${key}`}
                onClick={() => handleUnitChange(key)}
                className={`text-left px-5 py-6 border-r hairline last:border-r-0 transition-all duration-300 ${
                  isActive
                    ? "bg-[#1A3626] text-[#F4F2EC]"
                    : "bg-transparent text-[#1A3626] hover:bg-[#1A3626]/[0.04]"
                }`}
              >
                <div className="text-[10px] tracking-[0.24em] uppercase text-[#B3906A]">
                  {FLOORPLANS[key].sqm} sqm
                </div>
                <div className="mt-2 font-serif text-xl md:text-2xl leading-tight">
                  {FLOORPLANS[key].label}
                </div>
                <div
                  className={`mt-2 text-[11px] tracking-[0.18em] uppercase ${
                    isActive ? "text-[#F4F2EC]/65" : "text-[#6A736E]"
                  }`}
                >
                  {u?.priceLabel}
                </div>
              </button>
            );
          })}
        </div>

        {/* Floor toggle — dynamic */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div
            role="tablist"
            aria-label="Floor selector"
            className="inline-flex border hairline bg-white"
          >
            {availableFloors.map((f, i) => {
              const isActive = safeFloor === f;
              return (
                <button
                  key={f}
                  role="tab"
                  aria-selected={isActive}
                  data-testid={`floor-toggle-${f}`}
                  onClick={() => setFloor(f)}
                  className={`px-5 md:px-6 py-3 text-[11px] tracking-[0.24em] uppercase font-medium transition-colors ${
                    i > 0 ? "border-l hairline" : ""
                  } ${
                    isActive
                      ? "bg-[#1A3626] text-[#F4F2EC]"
                      : "text-[#1A3626] hover:bg-[#1A3626]/5"
                  }`}
                >
                  {FLOOR_META[f].label}
                </button>
              );
            })}
          </div>

          <div className="text-[11px] tracking-[0.24em] uppercase text-[#6A736E]">
            {FLOOR_META[safeFloor].level} / {FLOOR_META[safeFloor].focus}
          </div>
        </div>

        {/* Plan canvas */}
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 lg:col-span-8 bg-white border hairline relative overflow-hidden aspect-[4/4] md:aspect-[5/4]">
            <AnimatePresence mode="wait">
              <motion.img
                key={`${unitKey}-${safeFloor}`}
                src={imageMap[safeFloor]}
                alt={`${fp.label} / ${FLOOR_META[safeFloor].label}`}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.02 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="w-full h-full object-contain p-4 md:p-8"
                data-testid={`floorplan-image-${unitKey}-${safeFloor}`}
              />
            </AnimatePresence>
            <div className="absolute top-5 left-5 bg-[#F4F2EC] border hairline px-4 py-2.5">
              <div className="overline">{fp.label}</div>
              <div className="mt-1 font-serif text-base text-[#1A3626]">
                {fp.sqm} sqm BUA
              </div>
            </div>
            <div className="absolute bottom-5 right-5 text-[10px] tracking-[0.24em] uppercase text-[#6A736E] bg-[#F4F2EC]/85 backdrop-blur-sm px-3 py-2">
              {FLOOR_META[safeFloor].level}
            </div>
          </div>

          {/* Room schedule */}
          <div className="col-span-12 lg:col-span-4">
            <div className="overline mb-4">Schedule of accommodation</div>
            <AnimatePresence mode="wait">
              <motion.ul
                key={`${unitKey}-${safeFloor}-rooms`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4 }}
                className="space-y-0 border-y hairline"
              >
                {(roomsMap[safeFloor] || []).map((room, i) => (
                  <li
                    key={room}
                    data-testid={`floor-room-${i}`}
                    className="flex items-start gap-3 py-4 border-b hairline last:border-0"
                  >
                    <Check
                      className="w-4 h-4 mt-1 text-[#B3906A] flex-shrink-0"
                      strokeWidth={1.5}
                    />
                    <span className="font-serif text-lg text-[#1A3626] leading-snug">
                      {room}
                    </span>
                  </li>
                ))}
              </motion.ul>
            </AnimatePresence>

            <div className="mt-8 pt-6 border-t hairline">
              <div className="grid grid-cols-2 gap-4 mb-7">
                <Mini label="Bedrooms" value={fp.bedrooms} />
                <Mini label="Bathrooms" value={fp.bathrooms} />
                <Mini label="Parking" value={fp.parking} />
                <Mini label="Levels" value={fp.levels} />
              </div>
              <button
                onClick={() => scrollTo("contact")}
                data-testid="floorplans-cta"
                className="hge-btn-primary w-full justify-center"
              >
                Request Full Floor Plan
                <ArrowUpRight className="w-4 h-4" />
              </button>
              <p className="mt-4 text-[11px] text-[#6A736E] font-light leading-relaxed">
                Dimensioned PDF, materials &amp; finishes brochure available on
                EOI submission. Layout indicative. Modon reserves the right to
                amend.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Mini({ label, value }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.24em] uppercase text-[#6A736E]">
        {label}
      </div>
      <div className="mt-1 font-serif text-2xl text-[#1A3626]">{value}</div>
    </div>
  );
}
